import { Assets, ImageSource, Rectangle, Texture } from 'pixi.js';

/**
 * Reading the extractor's atlases.
 *
 * The metadata keeps the complete `SpriteInfo` for every appearance - all
 * pattern dimensions and the full sprite list - even though only some
 * directions were packed. That means the OTClient index formula works here
 * unchanged, and widening the extractor's scope later changes only which
 * frames exist, never how they are addressed.
 */

export type AssetCategory = 'creatures' | 'items' | 'effects' | 'missiles' | 'outfits' | 'mounts' | 'tiles';

/** [page, x, y, width, height] */
type Frame = [number, number, number, number, number];

export interface SpriteGroup {
  patternWidth: number;
  patternHeight: number;
  patternDepth: number;
  layers: number;
  frames: number;
  /** Per-phase [minMs, maxMs]. */
  phases: [number, number][];
  sprites: number[];
}

export interface AppearanceEntry {
  name?: string;
  groups: Record<string, SpriteGroup>;
}

export interface AtlasMeta {
  version?: number;
  category?: AssetCategory;
  /** Extractor run id; busts long-lived browser caches of the page files. */
  build?: string | number;
  directions?: number[] | null;
  atlas: { pages: string[]; pageSize: number; frames: Record<string, Frame> };
  entries: Record<string, AppearanceEntry>;
}

/** Idle, moving, and ground (tiles) are the groups the extractor packs. */
export const GROUP_IDLE = '0';
export const GROUP_MOVING = '1';
export const GROUP_GROUND = '2';

export const DIRECTION_NORTH = 0;
export const DIRECTION_EAST = 1;
/** 0 = north, 1 = east, 2 = south, 3 = west. */
export const DIRECTION_SOUTH = 2;
export const DIRECTION_WEST = 3;

const BASE = '/assets';

function applyPixelArtSampling(texture: Texture | null): void {
  if (!texture?.source || texture.source.destroyed) return;
  texture.source.scaleMode = 'nearest';
}

/**
 * A map page fetched outside Pixi's image queue (mapa cinza, 24/10): Pixi
 * decodes images in a pool of `hardwareConcurrency` workers, first come first
 * served, so on a phone the hunt map's pages waited behind the creature,
 * corpse and preload pages (measured on 3G: the first hunt's map page asked
 * 42 s after the cover went up). Same steps as Pixi's loader (fetch, blob,
 * createImageBitmap, premultiplied on upload), at high fetch priority.
 */
async function loadPageDirect(url: string, signal?: AbortSignal): Promise<Texture | null> {
  if (typeof createImageBitmap !== 'function') {
    return Assets.load<Texture>({ src: url, data: { scaleMode: 'nearest' } });
  }
  const response = await fetch(url, { priority: 'high', signal } as RequestInit);
  if (!response.ok) return null;
  const bitmap = await createImageBitmap(await response.blob());
  const source = new ImageSource({ resource: bitmap, alphaMode: 'premultiply-alpha-on-upload', scaleMode: 'nearest' });
  return new Texture({ source, label: url });
}

/**
 * Creature-like atlases pack all four directions of every outfit, which is
 * ~90 pages of 2048² for outfits alone. Those load page-by-page on demand
 * (only the pages an appearance on screen needs); the small static atlases
 * (items, tiles, effects, missiles) still load eagerly.
 */
const LAZY_CATEGORIES = new Set<AssetCategory>(['outfits', 'mounts', 'creatures']);

const metaLoads = new Map<AssetCategory, Promise<AtlasMeta>>();

/** Shared metadata fetch (Atlas and the DOM icon helpers reuse one download). */
export function loadAtlasMeta(category: AssetCategory): Promise<AtlasMeta> {
  const existing = metaLoads.get(category);
  if (existing) return existing;
  // Bust stale browser caches after atlas regenerations (old tiles.json left
  // OTBM rooms black because ground ids were missing from the cached meta).
  // 'no-cache' revalidates every time (ETag → 304), so an unchanged index is
  // not downloaded again on each visit ('no-store' re-sent ~2 MB of JSON).
  const promise = fetch(`${BASE}/${category}.json`, { cache: 'no-cache' })
    .then((r) => {
      if (!r.ok) throw new Error(`${category}.json ${r.status}`);
      return r.json() as Promise<AtlasMeta>;
    });
  promise.catch(() => metaLoads.delete(category));
  metaLoads.set(category, promise);
  return promise;
}

/**
 * Cache-busting token for an atlas's page files (changes on every
 * re-extract). Pixi and the DOM icon helpers use the same `?v=` so a page is
 * downloaded once and shared through the HTTP cache.
 */
const busts = new WeakMap<AtlasMeta, string>();
export function atlasBust(meta: AtlasMeta): string {
  // Memoised: icons ask for it per render, and Object.keys over 7k entries is not free.
  let bust = busts.get(meta);
  if (bust === undefined) {
    bust = meta.build !== undefined
      ? String(meta.build)
      : `${meta.atlas.pages.length}-${Object.keys(meta.entries).length}`;
    busts.set(meta, bust);
  }
  return bust;
}

export class Atlas {
  /** undefined = not requested yet, null = failed to load. */
  private readonly pages: (Texture | null | undefined)[];
  private readonly pageLoads = new Map<number, Promise<void>>();
  private readonly entryPages = new Map<number, number[]>();
  private readonly textures = new Map<number, Texture>();
  /** Pages fetched by `loadPageDirect` (world-map areas), not through Pixi's queue. */
  private direct = false;
  private signal: AbortSignal | undefined;
  private constructor(
    private readonly meta: AtlasMeta,
    private readonly baseUrl: string,
    private readonly bust: string,
    private readonly lazy: boolean,
  ) {
    this.pages = meta.atlas.pages.map(() => undefined);
  }

  static async load(category: AssetCategory): Promise<Atlas> {
    const meta = await loadAtlasMeta(category);
    const bust = atlasBust(meta);
    if (LAZY_CATEGORIES.has(category)) return new Atlas(meta, BASE, bust, true);
    return Atlas.fromMeta(meta, BASE, bust);
  }

  /**
   * Atlas whose metadata was fetched elsewhere (world-map areas embed theirs).
   * Pages resolve against `baseUrl`; `bust` changes when the files change.
   */
  static async fromMeta(
    meta: AtlasMeta,
    baseUrl: string,
    bust: string,
    /** Pages loaded so far (the "ENTERING WORLD" bar); failed pages count too. */
    onProgress?: (loaded: number, total: number) => void,
    /** Fetch the pages outside Pixi's image queue (the map of the place being entered). */
    direct = false,
    /** Stops the direct page fetches (a background town floor put off while a hunt is entered). */
    signal?: AbortSignal,
  ): Promise<Atlas> {
    const atlas = new Atlas(meta, baseUrl, bust, false);
    atlas.direct = direct;
    atlas.signal = signal;
    const total = meta.atlas.pages.length;
    let loaded = 0;
    onProgress?.(0, total);
    await Promise.all(meta.atlas.pages.map((_, index) => atlas.loadPage(index).then(() => {
      loaded += 1;
      onProgress?.(loaded, total);
    })));
    return atlas;
  }

  private loadPage(index: number): Promise<void> {
    if (this.pages[index] !== undefined) return Promise.resolve();
    const pending = this.pageLoads.get(index);
    if (pending) return pending;
    const file = this.meta.atlas.pages[index];
    const src = `${this.baseUrl}/${file}?v=${this.bust}`;
    const promise = (file
      ? (this.direct ? loadPageDirect(src, this.signal) : Assets.load<Texture>({ src, data: { scaleMode: 'nearest' } })).catch(() => null)
      : Promise.resolve(null)
    ).then((texture) => {
      applyPixelArtSampling(texture);
      this.pages[index] = texture;
      this.pageLoads.delete(index);
    });
    this.pageLoads.set(index, promise);
    return promise;
  }

  /** Atlas pages holding any sprite of an appearance (all groups/directions). */
  private pagesOf(id: number): number[] {
    const cached = this.entryPages.get(id);
    if (cached) return cached;
    const entry = this.entry(id);
    const pages = new Set<number>();
    if (entry) {
      for (const group of Object.values(entry.groups)) {
        for (const spriteId of group.sprites) {
          const frame = this.meta.atlas.frames[String(spriteId)];
          if (frame) pages.add(frame[0]);
        }
      }
    }
    const list = [...pages];
    this.entryPages.set(id, list);
    return list;
  }

  /** True when every page an appearance needs is in memory (always true for eager atlases). */
  isReady(id: number): boolean {
    if (!this.lazy) return true;
    return this.pagesOf(id).every((page) => this.pages[page] !== undefined);
  }

  /**
   * Load the pages for these appearances. Lazy atlases return empty frames for
   * an appearance until this resolves, so callers re-pose once it does.
   */
  ensure(ids: Iterable<number>): Promise<void> {
    if (!this.lazy) return Promise.resolve();
    const wanted = new Set<number>();
    for (const id of ids) {
      if (!id) continue;
      for (const page of this.pagesOf(id)) {
        if (this.pages[page] === undefined) wanted.add(page);
      }
    }
    if (!wanted.size) return Promise.resolve();
    return Promise.all([...wanted].map((page) => this.loadPage(page))).then(() => undefined);
  }

  /** Number of pages currently in memory (diagnostics). */
  get loadedPages(): number {
    return this.pages.filter((page) => page).length;
  }

  get category(): AssetCategory {
    return this.meta.category ?? 'tiles';
  }

  entry(id: number): AppearanceEntry | undefined {
    return this.meta.entries[String(id)];
  }

  has(id: number): boolean {
    return this.meta.entries[String(id)] !== undefined;
  }

  /** A single packed sprite, or null when it was not packed. */
  texture(spriteId: number): Texture | null {
    const cached = this.textures.get(spriteId);
    if (cached && !cached.destroyed && !cached.source.destroyed) return cached;
    if (cached) this.textures.delete(spriteId);

    const frame = this.meta.atlas.frames[String(spriteId)];
    if (!frame) return null;
    const [page, x, y, width, height] = frame;
    const source = this.pages[page];
    if (source === undefined) {
      // Lazy page: start fetching; callers re-pose after `ensure()` resolves.
      void this.loadPage(page);
      return null;
    }
    if (!source || source.destroyed || source.source.destroyed) return null;

    const texture = new Texture({
      source: source.source,
      frame: new Rectangle(x, y, width, height),
    });
    applyPixelArtSampling(texture);
    this.textures.set(spriteId, texture);
    return texture;
  }

  /**
   * Index into a group's sprite list.
   *
   * ((((phase x depth + mount) x height + addon) x width + direction) x layers + layer)
   */
  spriteIndex(
    group: SpriteGroup,
    options: { phase?: number; direction?: number; addon?: number; mount?: number; layer?: number } = {},
  ): number {
    const phase = (options.phase ?? 0) % Math.max(1, group.frames);
    const direction = (options.direction ?? 0) % Math.max(1, group.patternWidth);
    const addon = (options.addon ?? 0) % Math.max(1, group.patternHeight);
    const mount = (options.mount ?? 0) % Math.max(1, group.patternDepth);
    const layer = (options.layer ?? 0) % Math.max(1, group.layers);

    return (
      (((phase * group.patternDepth + mount) * group.patternHeight + addon) * group.patternWidth +
        direction) *
        group.layers +
      layer
    );
  }

  /**
   * Whether the extractor packed this direction for an appearance (checked on
   * the metadata, so it answers before any page is loaded). Older atlases
   * only packed south + west.
   */
  hasDirection(id: number, direction: number): boolean {
    const entry = this.entry(id);
    if (!entry) return false;
    const group = entry.groups[GROUP_MOVING] ?? entry.groups[GROUP_IDLE] ?? Object.values(entry.groups)[0];
    if (!group) return false;
    if (group.patternWidth <= direction) return false;
    const spriteId = group.sprites[this.spriteIndex(group, { direction })];
    return spriteId !== undefined && this.meta.atlas.frames[String(spriteId)] !== undefined;
  }

  /** Every phase of one direction, ready to hand to an AnimatedSprite. */
  frames(
    id: number,
    options: { group?: string; direction?: number; addon?: number; mount?: number; layer?: number } = {},
  ): { textures: Texture[]; durations: number[] } {
    const entry = this.entry(id);
    const empty = { textures: [] as Texture[], durations: [] as number[] };
    if (!entry) return empty;
    // Never hand out a partial pose (and its fallbacks) while pages stream in.
    if (!this.isReady(id)) {
      void this.ensure([id]);
      return empty;
    }

    const group =
      entry.groups[options.group ?? GROUP_IDLE] ??
      entry.groups[GROUP_IDLE] ??
      entry.groups[GROUP_GROUND] ??
      Object.values(entry.groups)[0];
    if (!group) return empty;

    const textures: Texture[] = [];
    const durations: number[] = [];
    for (let phase = 0; phase < Math.max(1, group.frames); phase += 1) {
      const index = this.spriteIndex(group, {
        phase,
        direction: options.direction,
        addon: options.addon,
        mount: options.mount,
        layer: options.layer,
      });
      const texture = this.texture(group.sprites[index] ?? -1);
      if (!texture) continue;
      textures.push(texture);
      // A missing phase table means a static appearance; 200 ms keeps anything
      // that turns out to be animated from strobing.
      const phaseTiming = group.phases[phase];
      durations.push(phaseTiming ? (phaseTiming[0] + phaseTiming[1]) / 2 : 200);
    }
    if (!textures.length) {
      const any = this.icon(id, options.direction ?? DIRECTION_SOUTH);
      if (any) return { textures: [any], durations: [200] };
    }
    return textures.length ? { textures, durations } : empty;
  }

  /** Best single texture for an appearance, for lists and inventory slots. */
  icon(id: number, direction = DIRECTION_SOUTH): Texture | null {
    const entry = this.entry(id);
    if (!entry) return null;
    if (!this.isReady(id)) {
      void this.ensure([id]);
      return null;
    }
    const group = entry.groups[GROUP_IDLE] ?? Object.values(entry.groups)[0];
    if (!group) return null;

    const direct = this.texture(group.sprites[this.spriteIndex(group, { direction })] ?? -1);
    if (direct) return direct;
    // Items are packed without direction variants, and creature scope may not
    // include the direction asked for; any packed frame beats a blank slot.
    for (const spriteId of group.sprites) {
      const texture = this.texture(spriteId);
      if (texture) return texture;
    }
    return null;
  }
}

const loading = new Map<AssetCategory, Promise<Atlas>>();

/** Atlases are large and immutable, so each category loads at most once. */
export function loadAtlas(category: AssetCategory): Promise<Atlas> {
  const existing = loading.get(category);
  if (existing) return existing;
  const promise = Atlas.load(category);
  loading.set(category, promise);
  return promise;
}
